/**
 * DTO shapes as the customers screens receive them.
 *
 * The masked variants are what an agent gets. The list row an owner receives has `email`,
 * `phone`, `lifetime_net_cents` and `rr_balance_cents`; the masked one does not have those
 * keys at all, which is why every one is optional here rather than nullable. A missing key
 * and a null value mean different things, and the UI must not treat them the same.
 */
import type {
  Address,
  CommunicationLogEntry,
  CustomerNote,
  CustomerSegment,
  OrderListRow,
  RrLedgerEntry,
} from '@elutide/shared';

export interface CustomerRow {
  customer_key: string;
  wp_user_id: number | null;
  display_name: string;
  orders_paid: number;
  first_paid_at: string | null;
  last_paid_at: string | null;
  segment: CustomerSegment;
  tags: string[];
  is_test: boolean;
  email?: string | null;
  phone?: string | null;
  email_masked?: string | null;
  phone_last4?: string | null;
  lifetime_net_cents?: number;
  rr_balance_cents?: number;
}

export interface DuplicateFlag {
  kind: 'same_phone' | 'near_name' | 'same_email_local';
  customer_key: string;
  display_name: string;
  detail: string;
  score: number;
}

export interface CustomerProfile extends CustomerRow {
  billing: Address | null;
  shipping: Partial<Address> | null;
  segment_reason: string;
  reorder_cadence_days: number | null;
  notes: CustomerNote[];
  recent_order_ids: number[];
  support_ticket_refs: string[];
  sms_opt_in: boolean;
  email_opt_in: boolean;
  duplicate_flags: DuplicateFlag[];
}

export interface Page<T> {
  items: T[];
  next_cursor: string | null;
  total_estimate: number | null;
}

export interface RewardsPage extends Page<RrLedgerEntry> {
  balance_cents: number;
}

export type CommunicationsPage = Page<CommunicationLogEntry>;
export type CustomerOrdersPage = Page<OrderListRow>;

export interface MessageTemplate {
  id: string;
  label: string;
  channels: ('email' | 'sms')[];
  subject: string | null;
  variables: string[];
}

/** True when the caller received the owner projection. Drives every "can I show money" test. */
export function hasFullContact(row: CustomerRow): boolean {
  return Object.prototype.hasOwnProperty.call(row, 'email');
}
